import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SuccessModal = ({ isOpen, onClose, customerData }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card rounded-lg shadow-modal max-w-2xl w-full">
        <div className="p-8 text-center">
          <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="CheckCircle2" size={48} color="var(--color-success)" />
          </div>

          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
            Antrag erfolgreich eingereicht!
          </h2>

          <p className="text-muted-foreground mb-8">
            Vielen Dank für Ihre Anmeldung zur THG-Prämie. Wir haben Ihren Antrag erhalten und werden ihn schnellstmöglich bearbeiten.
          </p>

          <div className="bg-gradient-to-br from-success via-success/90 to-success/80 border-4 border-success rounded-xl p-8 mb-6 shadow-2xl">
            <div className="flex items-start gap-5">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg animate-pulse">
                  <Icon name="FileCheck" size={40} color="var(--color-success)" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="font-heading font-bold text-white text-2xl">
                    ✓ Fahrzeugschein & FIN erfolgreich übermittelt
                  </h3>
                  <span className="px-3 py-1.5 bg-white rounded-full text-sm font-bold text-success shadow-md">
                    BESTÄTIGT
                  </span>
                </div>
                <div className="bg-white/95 rounded-lg p-5 space-y-4 shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                      <Icon name="Hash" size={22} color="var(--color-success)" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Fahrgestellnummer (FIN)</p>
                      <p className="text-lg font-mono font-bold text-success">{customerData?.chassisNumber}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                      <Icon name="FileText" size={22} color="var(--color-success)" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Fahrzeugschein</p>
                      <p className="text-base font-bold text-foreground">Hochgeladen und gespeichert</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-4 bg-white/90 rounded-lg border-2 border-white/50 shadow-md">
                  <div className="flex items-start gap-2">
                    <Icon name="ShieldCheck" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-foreground mb-1">Ihre Fahrzeugdaten sind sicher gespeichert</p>
                      <p className="text-xs text-muted-foreground">
                        Der Fahrzeugschein und die Fahrgestellnummer wurden erfolgreich in unserem System hinterlegt und werden ausschließlich für die Bearbeitung Ihres THG-Antrags verwendet.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-muted/50 rounded-lg p-6 mb-8 text-left">
            <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Info" size={20} color="var(--color-primary)" />
              Ihre Antragsdaten
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Kundennummer:</span>
                <span className="font-mono font-semibold text-foreground">{customerData?.customerNumber}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Name:</span>
                <span className="font-medium text-foreground">
                  {customerData?.firstname} {customerData?.lastname}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">E-Mail:</span>
                <span className="font-medium text-foreground">{customerData?.email}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Fahrgestellnummer:</span>
                <span className="font-mono text-foreground">{customerData?.chassisNumber}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Status:</span>
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-warning/10 text-warning rounded-full text-xs font-medium">
                  <span className="w-2 h-2 rounded-full bg-warning" />
                  Offen
                </span>
              </div>
            </div>
          </div>

          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-3 text-left">
              <Icon name="Mail" size={24} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-heading font-semibold text-foreground mb-2">
                  Bestätigungs-E-Mail
                </h4>
                <p className="text-sm text-muted-foreground">
                  Wir haben Ihnen eine Bestätigungs-E-Mail an <strong>{customerData?.email}</strong> gesendet. Bitte überprüfen Sie auch Ihren Spam-Ordner.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-heading font-semibold text-foreground">Wie geht es weiter?</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-primary">1</span>
                </div>
                <p className="text-muted-foreground">
                  Automatische Prüfung Ihrer Daten innerhalb von 2-3 Werktagen
                </p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-primary">2</span>
                </div>
                <p className="text-muted-foreground">
                  Weiterleitung an das Umweltbundesamt zur finalen Genehmigung
                </p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="font-bold text-primary">3</span>
                </div>
                <p className="text-muted-foreground">
                  Auszahlung der Prämie auf Ihr Konto nach erfolgreicher Prüfung
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-border p-6 flex justify-center">
          <Button
            variant="default"
            size="lg"
            onClick={onClose}
            iconName="Home"
            iconPosition="left"
          >
            Zur Startseite
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SuccessModal;